import {
  chakra,
  forwardRef,
  type TableRowProps as ChakraTableRowProps,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { useMemo } from 'react';

import { useTableStyles } from '../TableStylesContext';
import { useTableContext } from '../utils/context';
import { TrProvider } from '../utils/rowContext';
import { getClassName } from './className';

const RELAXED_PADDING_BY_SIZE: Record<string, number> = {
  sm: 0.75,
  md: 1,
};

type CssPropAssertedType = Record<string, unknown> & {
  ':hover:not(:has([role="group"]:hover))'?: Record<string, unknown>;
  ':active:not(:has([role="group"]:hover))'?: Record<string, unknown>;
};

export interface TrProps extends ChakraTableRowProps {
  isInteractive?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  index?: number;
}

export const Tr = forwardRef<TrProps, 'tr'>(
  (
    {
      isInteractive,
      isDisabled,
      isSelected,
      children,
      __css: cssProp,
      index,
      zebraIndex,
      className,
      ...restProps
    },
    ref,
  ) => {
    const { tr: trStyles } = useTableStyles() ?? {};

    const { zebra, density, size } = useTableContext() ?? {};

    const __css = useMemo(() => {
      // TODO: migrate cell style logic to Td component via new TrContext
      const innerCss: Record<string, unknown> = { ...trStyles, ...cssProp };

      if (zebra != null && !isSelected && index == null) {
        const zebraProps = {
          backgroundColor: 'page.plateLight',
          '--cell-background': 'var(--chakra-colors-page-plateLight)',
          ...((cssProp as CssPropAssertedType)?.[
            `:nth-of-type(${zebra}) > .table__td > *`
          ] ?? {}),
        };
        if (typeof zebraIndex === 'number') {
          const isEven = zebraIndex % 2 === 0;
          if (zebra === 'even' && isEven) {
            innerCss[`.table__td > *`] = zebraProps;
          } else if (zebra === 'odd' && !isEven) {
            innerCss[`.table__td > *`] = zebraProps;
          }
        } else {
          innerCss[`:nth-of-type(${zebra}) > .table__td > *`] = zebraProps;
        }
      }

      if (isSelected) {
        innerCss['> .table__td > *'] = {
          ...(isDisabled ? { opacity: 0.4 } : null),
          backgroundColor:
            'var(--row-active-bg, var(--chakra-colors-interaction-surfaceActive))',
          '--cell-background':
            'var(--row-active-bg, var(--chakra-colors-interaction-surfaceActive))',
          color: 'feature.primary',
          ...((cssProp as CssPropAssertedType)?.['> .table__td > *'] ?? {}),
        };

        innerCss['> .table__th'] = {
          backgroundColor:
            'var(--row-active-bg, var(--chakra-colors-interaction-active))',
          '--cell-background':
            'var(--row-active-bg, var(--chakra-colors-interaction-active))',
          color: 'feature.onInteraction',
          ...((cssProp as CssPropAssertedType)?.['> .table__th'] ?? {}),
        };
      }

      if (isInteractive) {
        innerCss.cursor = 'pointer';

        if (!isSelected) {
          innerCss[':hover:not(:has([role="group"]:hover))'] = {
            ...((cssProp as CssPropAssertedType)?.[
              ':hover:not(:has([role="group"]:hover))'
            ] ?? {}),
            '> .table__td > *': {
              backgroundColor:
                'var(--row-hover-bg, var(--chakra-colors-interaction-surfaceHover))',
              '--cell-background':
                'var(--row-hover-bg, var(--chakra-colors-interaction-surfaceHover))',
              color: 'feature.primary',
              ...((cssProp as CssPropAssertedType)?.[
                ':hover:not(:has([role="group"]:hover))'
              ]?.['> .table__td > *'] ?? {}),
            },
          };

          innerCss[':active:not(:has([role="group"]:hover))'] = {
            ...((cssProp as CssPropAssertedType)?.[
              ':active:not(:has([role="group"]:hover))'
            ] ?? {}),
            '> .table__td > *': {
              backgroundColor:
                'var(--row-active-bg, var(--chakra-colors-interaction-surfaceActive))',
              '--cell-background':
                'var(--row-active-bg, var(--chakra-colors-interaction-surfaceActive))',
              ...((cssProp as CssPropAssertedType)?.[
                ':active:not(:has([role="group"]:hover))'
              ]?.['> .table__td > *'] ?? {}),
            },
          };

          innerCss['> .table__td > *'] = {
            ...(isDisabled ? { opacity: 0.4 } : null),
            ...((cssProp as CssPropAssertedType)?.['> .table__td > *'] ?? {}),
          };
        }

        innerCss[':focus-visible'] = {
          boxShadow: 'focusInset',
          outline: 'none',
          ...((cssProp as CssPropAssertedType)?.[':focus-visible'] ?? {}),
        };
      }

      if (density === 'relaxed') {
        innerCss._notLast = {
          '.table__td': {
            borderBottomWidth: 0,
          },
        };
        innerCss._first = {
          '.table__td': {
            pt: RELAXED_PADDING_BY_SIZE[size],
          },
        };
        innerCss._last = {
          '.table__td': {
            pb: RELAXED_PADDING_BY_SIZE[size],
          },
        };
      }
      return innerCss;
    }, [
      trStyles,
      cssProp,
      size,
      density,
      zebra,
      index,
      isSelected,
      isInteractive,
      isDisabled,
      zebraIndex,
    ]);

    const trContextValue = useMemo(
      () => ({ index, isInteractive }),
      [index, isInteractive],
    );

    return (
      <chakra.tr
        ref={ref}
        __css={__css}
        className={cx(getClassName('tr'), className)}
        tabIndex={isInteractive ? 0 : undefined}
        {...restProps}
      >
        <TrProvider value={trContextValue}>{children}</TrProvider>
      </chakra.tr>
    );
  },
);
Tr.displayName = 'Tr';
